import { useCallback, useEffect } from 'react';
import type {
  FieldValues,
  Path,
  PathValue,
  UseFormReturn,
} from 'react-hook-form';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import type { FilterOption } from '~/modules/settings/types/filter';

interface UseFilterDrawerProps<T extends FieldValues> {
  open: boolean;
  onApply: () => void;
  filterForm: {
    getValues: UseFormReturn<T>['getValues'];
    setValue: UseFormReturn<T>['setValue'];
    reset: UseFormReturn<T>['reset'];
  };
}

export const useFilterDrawer = <T extends FieldValues>({
  open,
  onApply,
  filterForm,
}: UseFilterDrawerProps<T>) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isFilterKey = useCallback(
    (key: string): key is Path<T> => {
      const defaultValues = filterForm.getValues ? filterForm.getValues() : {};

      return key in defaultValues;
    },
    [filterForm],
  );

  useEffect(() => {
    if (open) {
      for (const [key, value] of searchParams) {
        if (isFilterKey(key) && filterForm.setValue) {
          const formValue = filterForm.getValues(key);

          if (Array.isArray(formValue)) {
            filterForm.setValue(
              key,
              value.split(',').map((val) => ({ value: val })) as PathValue<
                T,
                Path<T>
              >,
            );
          } else {
            filterForm.setValue(key, value as PathValue<T, Path<T>>);
          }
        }
      }
    }
  }, [filterForm, open, searchParams, isFilterKey]);

  const handleClear = useCallback(() => {
    filterForm.reset();
  }, [filterForm]);

  const handleApply = useCallback(
    (filterData: T) => {
      const params = new URLSearchParams();

      for (const [key, filter] of Object.entries(
        filterData as Record<string, string | FilterOption[]>,
      )) {
        if (Array.isArray(filter) && filter.length) {
          params.set(
            key,
            filter.map((item: { value: string }) => item.value).join(','),
          );
        } else if (filter && !Array.isArray(filter) && filter !== 'all') {
          params.set(key, filter);
        } else if (filter === 'all' && params.has(key)) {
          params.delete(key);
        }
      }

      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true },
      );
      onApply();
    },
    [navigate, location, onApply],
  );

  return {
    handleClear,
    handleApply,
  };
};
